import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export type UserRole = 'super_admin' | 'admin' | 'booking_manager' | 'content_manager' | 'support_agent' | 'client' | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: UserRole;
  loading: boolean;
  mustChangePassword: boolean;
  setMustChangePassword: (value: boolean) => void;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  // Permission helper functions
  isStaff: () => boolean;
  hasAdminAccess: () => boolean;
  hasContentAccess: () => boolean;
  hasBookingAccess: () => boolean;
  hasSupportAccess: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error fetching user role:', error);
        return null;
      }

      return data?.role as UserRole;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
  };

  const checkMustChangePassword = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('must_change_password')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error checking password change requirement:', error);
        return false;
      }

      return data?.must_change_password ?? false;
    } catch (error) {
      console.error('Error checking password change requirement:', error);
      return false;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer role fetching with setTimeout to prevent deadlock
        if (session?.user) {
          setTimeout(() => {
            fetchUserRole(session.user.id).then(setRole);
            checkMustChangePassword(session.user.id).then(setMustChangePassword);
          }, 0);
        } else {
          setRole(null);
          setMustChangePassword(false);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserRole(session.user.id).then(setRole);
        checkMustChangePassword(session.user.id).then(setMustChangePassword);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole(null);
    setMustChangePassword(false);
  };

  // Permission helper functions
  const isStaff = () => {
    return ['super_admin', 'admin', 'booking_manager', 'content_manager', 'support_agent'].includes(role || '');
  };

  const hasAdminAccess = () => {
    return ['super_admin', 'admin'].includes(role || '');
  };

  const hasContentAccess = () => {
    return ['super_admin', 'admin', 'content_manager'].includes(role || '');
  };

  const hasBookingAccess = () => {
    return ['super_admin', 'admin', 'booking_manager'].includes(role || '');
  };

  const hasSupportAccess = () => {
    return ['super_admin', 'admin', 'support_agent', 'booking_manager'].includes(role || '');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      role, 
      loading, 
      mustChangePassword, 
      setMustChangePassword, 
      signUp, 
      signIn, 
      signOut,
      isStaff,
      hasAdminAccess,
      hasContentAccess,
      hasBookingAccess,
      hasSupportAccess
    }}>
      {children}
    </AuthContext.Provider>
  );
};
